import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, CardModal, useShowError, ValidatedInput } from '@aserto/console-common'
import { Query, useQueryClient } from '@tanstack/react-query'

import { Configuration } from '../../../../common/types'
import { useAddConfiguration } from '../../../api/internal'
import useIsConfigurationNameAvailable from '../../../lib/availability/UseIsConfigurationNameAvailable'
import AsertoNameValidator from '../../../lib/validation/AsertoNameValidator'
import { useActiveConfigurationName } from '../../../services/ActiveConfiguration'
import { QueryKeys } from '../../../types/local/general'
import { ButtonsContainer, ContentContainer, FieldContainer, FormContainer } from './styles'

type ConfigurationModalProps = {
  configuration?: Configuration
  show: boolean
  onSuccess?: () => void
  onHide: () => void
}

const ConfigurationModal: React.FC<ConfigurationModalProps> = ({ configuration, show, onHide }) => {
  const [instanceName, setInstanceName] = useState('')
  const clearModalState = () => {
    setInstanceName('')
  }

  const showError = useShowError()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setConfigurationName } = useActiveConfigurationName()
  const { mutate: addConfiguration } = useAddConfiguration({
    onError: (e) => {
      showError(e)
    },
    onSuccess: (_data, variables) => {
      setConfigurationName((variables as Configuration).name)
      navigate('/ui/settings', {
        state: {
          configuration: {
            authorizerServiceUrl: '',
            authorizerApiKey: '',
            directoryServiceUrl: '',
            directoryApiKey: '',
            directoryTenantId: '',
            directoryReaderServiceUrl: '',
            directoryWriterServiceUrl: '',
            directoryModelServiceUrl: '',
          },
        },
      })
      queryClient.removeQueries({
        predicate: (query: Query) => {
          return !query.queryKey.includes(QueryKeys.Configurations)
        },
      })
    },
  })

  return (
    <>
      <CardModal
        cardHeight="100%"
        show={show}
        title="Add a configuration"
        onHide={() => {
          clearModalState()
          onHide()
        }}
      >
        <ContentContainer className="add-a-authorizer-modal-content">
          <div>Provide a unique name and click the Save button.</div>
          <FormContainer>
            <FieldContainer>
              <ValidatedInput
                autoFocus
                defaultValue={configuration?.name}
                disabled={!!configuration?.name}
                label="Instance Name"
                useIsAvailable={useIsConfigurationNameAvailable()}
                validator={AsertoNameValidator}
                value={configuration?.name}
                onChange={(v) => setInstanceName(v || '')}
              />
            </FieldContainer>
          </FormContainer>
          <ButtonsContainer>
            <Button
              data-testid="cancel-btn"
              variant="secondary"
              onClick={() => {
                clearModalState()
                onHide()
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="add-authorizer-modal-btn"
              disabled={instanceName === ''}
              id="add-authorizer"
              type="submit"
              onClick={() => {
                onHide()
                addConfiguration({ name: instanceName })
              }}
            >
              Save
            </Button>
          </ButtonsContainer>
        </ContentContainer>
      </CardModal>
    </>
  )
}

export default ConfigurationModal
